import { getPaymentPeriods, roundOfferFactor } from "@/utils/offerCalculations";
import type { RefinanceOffer, RefinanceOfferPayload } from "@/types/underwriting";

export interface RefinanceCalcValues {
  refiAmount: number;
  factor: number;
  payment: number;
  balance: number;
  term: number;
  frequency: string;
}

export const REFINANCE_CALC_DEFAULTS: RefinanceCalcValues = {
  refiAmount: 0,
  factor: 1.49,
  payment: 0,
  balance: 0,
  term: 60,
  frequency: "daily",
};

export function refinanceValuesFromOffer(
  offer: RefinanceOffer | null | undefined,
  seed: Partial<RefinanceCalcValues> = {}
): RefinanceCalcValues {
  const base = { ...REFINANCE_CALC_DEFAULTS, ...seed };
  if (!offer) return { ...base };
  return {
    refiAmount: Number(offer.refiAmount ?? 0) || 0,
    factor: roundOfferFactor(Number(offer.factor ?? 0) || base.factor),
    payment: Number(offer.merchantInstallment ?? 0) || 0,
    balance: Number(offer.balance ?? 0) || 0,
    term: Number(offer.term ?? 0) || base.term,
    frequency: offer.frequency || base.frequency,
  };
}

export function refinanceValuesToPayload(values: RefinanceCalcValues): RefinanceOfferPayload {
  return {
    refiAmount: values.refiAmount || null,
    factor: roundOfferFactor(values.factor) || null,
    merchantInstallment: values.payment || null,
    balance: values.balance || null,
    term: values.term || null,
    frequency: values.frequency,
  };
}

export function refinancePayloadsEqual(
  a: RefinanceOfferPayload,
  b: RefinanceOfferPayload
): boolean {
  return (
    Number(a.refiAmount ?? 0) === Number(b.refiAmount ?? 0) &&
    Number(a.factor ?? 0) === Number(b.factor ?? 0) &&
    Number(a.merchantInstallment ?? 0) === Number(b.merchantInstallment ?? 0) &&
    Number(a.balance ?? 0) === Number(b.balance ?? 0) &&
    Number(a.term ?? 0) === Number(b.term ?? 0) &&
    (a.frequency || "") === (b.frequency || "")
  );
}

/** Refi Amount ⇄ Payment stay linked via factor/term/frequency (same as Offer Builder). */
export function paymentFromRefiAmount(
  refiAmount: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number
): number {
  const periods = getPaymentPeriods(term, frequency, businessDaysPerMonth);
  if (periods <= 0) return 0;
  return Math.ceil((refiAmount * factor) / periods);
}

export function refiAmountFromPayment(
  payment: number,
  factor: number,
  term: number,
  frequency: string,
  businessDaysPerMonth: number
): number {
  if (factor <= 0) return 0;
  const periods = getPaymentPeriods(term, frequency, businessDaysPerMonth);
  return Math.round((payment * periods) / factor);
}

export type RefinanceDriver = "refiAmount" | "payment";

/** Re-link Refi Amount ⇄ Payment after factor/term/frequency change. Before either
 * is edited, a user edit of those terms drives Payment from Refi Amount; hydration
 * (nothing edited, nothing touched) leaves a saved payment alone. */
export function relinkRefinanceValues(
  values: RefinanceCalcValues,
  lastEdited: RefinanceDriver | null,
  termsTouched: boolean,
  businessDaysPerMonth: number
): RefinanceCalcValues {
  const driver = lastEdited ?? (termsTouched ? "refiAmount" : null);
  const { refiAmount, payment, factor, term, frequency } = values;
  if (driver === "refiAmount") {
    return {
      ...values,
      payment: paymentFromRefiAmount(refiAmount, factor, term, frequency, businessDaysPerMonth),
    };
  }
  if (driver === "payment") {
    return {
      ...values,
      refiAmount: refiAmountFromPayment(payment, factor, term, frequency, businessDaysPerMonth),
    };
  }
  return values;
}

export function formatRefinanceMoneyInput(val: number): string {
  const n = Number(val);
  if (!n || isNaN(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  return rounded % 1 === 0
    ? rounded.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function parseMoneyInput(raw: string): number {
  return Number(String(raw).replace(/,/g, "")) || 0;
}

export function refinancePayback(refiAmount: number, factor: number): number {
  return Math.round(refiAmount * factor * 100) / 100;
}

export function refinanceDisbursed(
  refiAmount: number,
  balance: number,
  originationPct: number
): number {
  return Math.round((refiAmount * (1 - originationPct / 100) - balance) * 100) / 100;
}

export function refinanceBalanceError(
  balance: number,
  refiAmount: number,
  originationPct: number,
  maxBalancePriorRtr: number | null = null
): string | null {
  if (!(balance > 0)) return null;
  const maxDisbursable = refiAmount > 0 ? refiAmount * (1 - originationPct / 100) : null;
  if (maxDisbursable != null && balance > maxDisbursable + 1e-9) {
    return `Balance cannot exceed funding after origination (max $${formatRefinanceMoneyInput(maxDisbursable)}).`;
  }
  if (
    maxBalancePriorRtr != null &&
    Number.isFinite(maxBalancePriorRtr) &&
    balance > maxBalancePriorRtr + 1e-9
  ) {
    return `Balance cannot exceed the prior contract's total RTR ($${formatRefinanceMoneyInput(maxBalancePriorRtr)}).`;
  }
  return null;
}
